<template>
  <section class="page" data-testid="page-tts">
    <header class="page-header">
      <div><h1>TTS</h1><p>音色、绑定和合成测试。</p></div>
      <div class="actions">
        <button class="button" :disabled="!canCopyDiagnostics" @click="copyDiagnostics">复制诊断</button>
        <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>
    <div class="metrics-grid">
      <MetricCard label="音色" :value="voices.length" />
      <MetricCard label="当前匹配" :value="filteredVoices.length" />
      <MetricCard label="Edge" :value="voiceMetrics.edge" />
      <MetricCard label="CosyVoice2" :value="voiceMetrics.cosy" />
      <MetricCard label="有参考音频" :value="voiceMetrics.refAudio" />
      <MetricCard label="人设绑定" :value="bindingRows.length" />
      <MetricCard label="默认音色" :value="defaultVoice || '-'" />
      <MetricCard label="参考音频目标" :value="refVoiceName || '-'" />
    </div>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <p v-if="error" class="error-text">{{ error }}</p>
    <div class="panel">
      <h2>创建音色</h2>
      <div class="form-grid">
        <FormField label="名称"><input v-model="form.name" /></FormField>
        <FormField label="引擎"><select v-model="form.engine"><option value="edge-tts">edge-tts</option><option value="cosyvoice2">cosyvoice2</option></select></FormField>
        <FormField label="Edge Voice"><input v-model="form.edge_voice" /></FormField>
        <FormField label="描述"><input v-model="form.description" /></FormField>
      </div>
      <button class="button primary" :disabled="saving" @click="createVoice">{{ saving ? "创建中..." : "创建" }}</button>
    </div>
    <div class="panel">
      <h2>参考音频</h2>
      <div class="form-grid">
        <FormField label="音色">
          <select v-model="refVoiceName">
            <option v-for="voice in voices" :key="voice.name" :value="voice.name">{{ voice.name }}</option>
          </select>
        </FormField>
        <FormField label="Prompt Text"><input v-model="refPromptText" /></FormField>
      </div>
      <input type="file" accept="audio/*" :disabled="!refVoiceName || saving" @change="uploadRef" />
    </div>
    <div class="panel">
      <h2>音色列表</h2>
      <div class="toolbar">
        <input v-model="voiceQuery" placeholder="搜索音色、描述、Edge Voice" />
        <button class="button" :class="{ primary: engineFilter === 'all' }" @click="engineFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: engineFilter === 'edge-tts' }" @click="engineFilter = 'edge-tts'">Edge</button>
        <button class="button" :class="{ primary: engineFilter === 'cosyvoice2' }" @click="engineFilter = 'cosyvoice2'">Cosy</button>
        <button class="button" :class="{ primary: engineFilter === 'ref' }" @click="engineFilter = 'ref'">有参考音频</button>
      </div>
      <div class="toolbar">
        <input v-model="testText" placeholder="测试文本" />
      </div>
      <DataTable :columns="columns" :rows="filteredVoices" row-key="name">
        <template #engine="{ row }"><span class="status-badge" :class="row.engine === 'cosyvoice2' ? 'degraded' : 'ok'">{{ row.engine }}</span></template>
        <template #description="{ row }">
          <div class="voice-cell">
            <span>{{ row.description || "-" }}</span>
            <small>{{ row.edge_voice || row.ref_audio || "-" }}</small>
          </div>
        </template>
        <template #actions="{ row }">
          <div class="row-actions">
            <button class="button small" @click="selectVoice(row)">详情</button>
            <button class="button small" :disabled="testing === row.name" data-testid="tts-test-submit" @click="test(row.name)">{{ testing === row.name ? "测试中" : "测试" }}</button>
            <button class="button small" @click="copyVoice(row)">复制</button>
            <button class="button small danger" @click="remove(row.name)">删除</button>
          </div>
        </template>
      </DataTable>
      <div v-if="testPayload" class="test-summary" :class="testPayload.fallback_used ? 'warning' : 'ok'">
        <strong>{{ testPayload.fallback_used ? "已使用 fallback 合成" : "目标引擎合成成功" }}</strong>
        <span>{{ testSummary }}</span>
      </div>
      <audio v-if="audioUrl" :src="audioUrl" controls />
      <details v-if="testDiagnostics" class="raw-result">
        <summary>测试诊断</summary>
        <button class="button small" @click="copyTestDiagnostics">复制测试诊断</button>
        <pre>{{ testDiagnostics }}</pre>
      </details>
    </div>
    <div class="split">
      <div class="panel">
        <h2>音色详情</h2>
        <div v-if="selectedVoice" class="voice-detail">
          <div class="runtime-grid compact">
            <div><span>名称</span><strong>{{ selectedVoice.name }}</strong></div>
            <div><span>引擎</span><strong>{{ selectedVoice.engine }}</strong></div>
            <div><span>Edge Voice</span><strong>{{ selectedVoice.edge_voice || "-" }}</strong></div>
            <div><span>参考音频</span><strong>{{ selectedVoice.ref_audio || "-" }}</strong></div>
          </div>
          <pre>{{ JSON.stringify(selectedVoice, null, 2) }}</pre>
        </div>
        <div v-else class="empty">选择一个音色查看完整配置。</div>
      </div>
      <div class="panel">
        <h2>绑定建议</h2>
        <div class="diagnostic-list">
          <div v-for="item in bindingDiagnostics" :key="item.label" class="diagnostic-row" :class="item.level">
            <strong>{{ item.label }}</strong>
            <span>{{ item.detail }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="panel">
      <h2>人设绑定</h2>
      <div class="form-grid">
        <FormField label="人设">
          <select v-model="bindingPersonaSelection" @change="applyBindingPersonaSelection">
            <option value="">选择人设</option>
            <option v-for="persona in personas" :key="persona" :value="persona">{{ persona }}</option>
            <option value="__custom">自定义</option>
          </select>
        </FormField>
        <FormField v-if="bindingPersonaSelection === '__custom'" label="自定义人设"><input v-model="binding.persona_name" /></FormField>
        <FormField label="音色">
          <select v-model="binding.voice_name">
            <option value="">解绑</option>
            <option v-for="voice in voices" :key="voice.name" :value="voice.name">{{ voice.name }}</option>
          </select>
        </FormField>
      </div>
      <button class="button primary" :disabled="saving || !binding.persona_name" @click="bind">{{ saving ? "保存中..." : "保存绑定" }}</button>
      <div class="toolbar">
        <input v-model="bindingQuery" placeholder="搜索人设或音色" />
        <button class="button" :class="{ primary: bindingFilter === 'all' }" @click="bindingFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: bindingFilter === 'custom' }" @click="bindingFilter = 'custom'">已绑定</button>
        <button class="button" :class="{ primary: bindingFilter === 'default' }" @click="bindingFilter = 'default'">默认</button>
      </div>
      <DataTable :columns="bindingColumns" :rows="filteredBindingRows" row-key="persona_name">
        <template #voice_name="{ row }"><span>{{ row.voice_name || "默认" }}</span></template>
        <template #actions="{ row }">
          <div class="row-actions">
            <button class="button small" @click="editBinding(row)">编辑</button>
            <button class="button small" @click="copyBinding(row)">复制</button>
            <button class="button small danger" :disabled="saving" @click="unbind(row.persona_name)">解绑</button>
          </div>
        </template>
      </DataTable>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import type { VoiceProfile } from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const voices = ref<VoiceProfile[]>([]);
const bindings = ref<Record<string, string>>({});
const personas = ref<string[]>([]);
const defaultVoice = ref("");
const audioUrl = ref("");
const testDiagnostics = ref("");
const testPayload = ref<any | null>(null);
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择诊断内容");
const loading = ref(false);
const saving = ref(false);
const testing = ref("");
const voiceQuery = ref("");
const bindingQuery = ref("");
const engineFilter = ref<"all" | "edge-tts" | "cosyvoice2" | "ref">("all");
const bindingFilter = ref<"all" | "custom" | "default">("all");
const selectedVoice = ref<VoiceProfile | null>(null);
const testText = ref("你好，这是一段测试语音。");
const refVoiceName = ref("");
const refPromptText = ref("");
const bindingPersonaSelection = ref("");
const form = reactive({ name: "", engine: "edge-tts", description: "", edge_voice: "zh-CN-YunxiNeural" });
const binding = reactive({ persona_name: "", voice_name: "" });
const columns = [
  { key: "name", label: "名称" },
  { key: "engine", label: "引擎" },
  { key: "description", label: "描述" },
  { key: "actions", label: "操作" }
];
const bindingColumns = [
  { key: "persona_name", label: "人设" },
  { key: "voice_name", label: "音色" },
  { key: "actions", label: "操作" }
];
const bindingRows = computed(() => Object.entries(bindings.value).map(([persona_name, voice_name]) => ({ persona_name, voice_name })));
const filteredVoices = computed(() => {
  const text = voiceQuery.value.trim().toLowerCase();
  return voices.value.filter((voice) => {
    if (engineFilter.value === "edge-tts" && voice.engine !== "edge-tts") return false;
    if (engineFilter.value === "cosyvoice2" && voice.engine !== "cosyvoice2") return false;
    if (engineFilter.value === "ref" && !voice.ref_audio) return false;
    if (!text) return true;
    return [voice.name, voice.engine, voice.description, voice.edge_voice, voice.ref_audio, voice.prompt_text].join(" ").toLowerCase().includes(text);
  });
});
const filteredBindingRows = computed(() => {
  const text = bindingQuery.value.trim().toLowerCase();
  return bindingRows.value.filter((row) => {
    if (bindingFilter.value === "custom" && !row.voice_name) return false;
    if (bindingFilter.value === "default" && row.voice_name) return false;
    if (!text) return true;
    return `${row.persona_name} ${row.voice_name}`.toLowerCase().includes(text);
  });
});
const voiceMetrics = computed(() => ({
  edge: voices.value.filter((voice) => voice.engine === "edge-tts").length,
  cosy: voices.value.filter((voice) => voice.engine === "cosyvoice2").length,
  refAudio: voices.value.filter((voice) => !!voice.ref_audio).length
}));
const canCopyDiagnostics = computed(() => voices.value.length || bindingRows.value.length || !!testPayload.value);
const testSummary = computed(() => {
  if (!testPayload.value) return "";
  return [
    `attempted=${testPayload.value.engine_attempted || "-"}`,
    `used=${testPayload.value.engine_used || "-"}`,
    `edge=${testPayload.value.edge_available ? "ok" : "down"}`,
    `cosy=${testPayload.value.cosyvoice_available ? "ok" : "down"}`
  ].join(" / ");
});
const bindingDiagnostics = computed(() => {
  const missing = personas.value.filter((persona) => !bindings.value[persona]);
  const invalid = bindingRows.value.filter((row) => row.voice_name && !voices.value.some((voice) => voice.name === row.voice_name));
  return [
    {
      label: "默认音色",
      detail: defaultVoice.value ? `未绑定人设会使用 ${defaultVoice.value}` : "未配置默认音色",
      level: defaultVoice.value ? "ok" : "warning"
    },
    {
      label: "未绑定人设",
      detail: missing.length ? `${missing.length} 个人设将走默认音色` : "所有已知人设都有显式绑定",
      level: missing.length ? "warning" : "ok"
    },
    {
      label: "失效绑定",
      detail: invalid.length ? invalid.map((row) => `${row.persona_name}:${row.voice_name}`).join(", ") : "未发现失效绑定",
      level: invalid.length ? "error" : "ok"
    }
  ];
});

function syncBindingPersonaSelection() {
  if (!binding.persona_name) {
    bindingPersonaSelection.value = "";
  } else if (personas.value.includes(binding.persona_name)) {
    bindingPersonaSelection.value = binding.persona_name;
  } else {
    bindingPersonaSelection.value = "__custom";
  }
}
function applyBindingPersonaSelection() {
  if (bindingPersonaSelection.value === "__custom") return;
  binding.persona_name = bindingPersonaSelection.value;
  binding.voice_name = bindings.value[binding.persona_name] || "";
}
function editBinding(row: { persona_name: string; voice_name: string }) {
  binding.persona_name = row.persona_name;
  binding.voice_name = row.voice_name;
  syncBindingPersonaSelection();
}
function selectVoice(voice: VoiceProfile) {
  selectedVoice.value = voice;
  refVoiceName.value = voice.name;
}
async function copyVoice(voice: VoiceProfile) {
  await copyJson(voice, "音色配置已复制");
}
async function copyBinding(row: { persona_name: string; voice_name: string }) {
  await copyJson(row, "绑定配置已复制");
}
async function copyTestDiagnostics() {
  if (!testPayload.value) return;
  await copyJson(testPayload.value, "测试诊断已复制");
}
async function copyDiagnostics() {
  await copyJson({
    voices: filteredVoices.value,
    bindings: filteredBindingRows.value,
    default_voice: defaultVoice.value,
    test: testPayload.value,
    diagnostics: bindingDiagnostics.value
  }, "TTS 诊断已复制");
}
async function load(announce = true) {
  loading.value = true;
  error.value = "";
  if (announce) actionResult.value = "";
  try {
    const payload = await api<{ voices: VoiceProfile[]; default_voice?: string }>("/api/tts/voices");
    voices.value = payload.voices || [];
    defaultVoice.value = payload.default_voice || "";
    if (!refVoiceName.value && voices.value[0]) refVoiceName.value = voices.value[0].name;
    if (selectedVoice.value && !voices.value.some((voice) => voice.name === selectedVoice.value?.name)) {
      selectedVoice.value = null;
    } else if (!selectedVoice.value) {
      selectedVoice.value = voices.value[0] || null;
    } else {
      selectedVoice.value = voices.value.find((voice) => voice.name === selectedVoice.value?.name) || selectedVoice.value;
    }
    const b = await api<{ bindings: Record<string, string>; default_voice?: string }>("/api/tts/persona-bindings");
    bindings.value = b.bindings || {};
    defaultVoice.value = b.default_voice || defaultVoice.value;
    personas.value = (await api<{ personas: string[] }>("/api/personas")).personas || [];
    syncBindingPersonaSelection();
    if (announce) actionResult.value = `已加载 ${voices.value.length} 个音色`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载 TTS 配置失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function createVoice() {
  if (!form.name.trim()) {
    error.value = "音色名称不能为空";
    return;
  }
  saving.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/tts/voices", form);
    actionResult.value = "音色已创建";
    toast.push("音色已创建");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "创建音色失败";
    toast.push(error.value, "error");
  } finally {
    saving.value = false;
  }
}
async function test(name: string) {
  testing.value = name;
  testDiagnostics.value = "";
  testPayload.value = null;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>(`/api/tts/voices/${encodeURIComponent(name)}/test`, { text: testText.value || "你好，这是一段测试语音。" });
    testPayload.value = payload;
    audioUrl.value = payload.audio_url;
    testDiagnostics.value = JSON.stringify(payload, null, 2);
    actionResult.value = payload.fallback_used ? "合成成功，使用 fallback" : "合成成功";
    toast.push(payload.fallback_used ? "合成成功，使用 fallback" : "合成成功");
  } catch (err) {
    error.value = err instanceof Error ? err.message : "测试合成失败";
    toast.push(error.value, "error");
  } finally {
    testing.value = "";
  }
}
async function remove(name: string) {
  if (!window.confirm(`确定删除音色 ${name}？`)) return;
  saving.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await api(`/api/tts/voices/${encodeURIComponent(name)}`, { method: "DELETE" });
    actionResult.value = "音色已删除";
    toast.push("已删除");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "删除音色失败";
    toast.push(error.value, "error");
  } finally {
    saving.value = false;
  }
}
async function bind() {
  saving.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/tts/persona-bindings", binding);
    actionResult.value = "绑定已保存";
    toast.push("绑定已保存");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "保存绑定失败";
    toast.push(error.value, "error");
  } finally {
    saving.value = false;
  }
}
async function unbind(personaName: string) {
  saving.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/tts/persona-bindings", { persona_name: personaName, voice_name: "" });
    actionResult.value = "绑定已移除";
    toast.push("绑定已移除");
    if (binding.persona_name === personaName) binding.voice_name = "";
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "解绑失败";
    toast.push(error.value, "error");
  } finally {
    saving.value = false;
  }
}
async function uploadRef(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file || !refVoiceName.value) return;
  const body = new FormData();
  body.append("file", file);
  body.append("prompt_text", refPromptText.value);
  saving.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await api(`/api/tts/voices/${encodeURIComponent(refVoiceName.value)}/upload-ref`, { method: "POST", body });
    actionResult.value = "参考音频已上传";
    toast.push("参考音频已上传");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "上传参考音频失败";
    toast.push(error.value, "error");
  } finally {
    saving.value = false;
    (event.target as HTMLInputElement).value = "";
  }
}
onMounted(load);
</script>

<style scoped>
.toolbar input {
  min-width: min(360px, 100%);
  flex: 1 1 260px;
}

.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.voice-cell {
  display: grid;
  gap: 4px;
  min-width: 160px;
}

.voice-cell small {
  color: var(--muted);
}

.row-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.test-summary {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
  margin-top: 12px;
}

.test-summary strong,
.test-summary span {
  display: block;
}

.test-summary span {
  color: var(--muted);
  margin-top: 5px;
}

.test-summary.ok {
  border-color: #386f47;
}

.test-summary.warning {
  border-color: #78612d;
}

.raw-result {
  margin-top: 12px;
}

.runtime-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
}

.runtime-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}

.runtime-grid span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.runtime-grid strong {
  display: block;
  margin-top: 5px;
  word-break: break-all;
}

.runtime-grid.compact {
  margin-bottom: 12px;
}

.voice-detail {
  display: grid;
  gap: 12px;
}

.diagnostic-list {
  display: grid;
  gap: 10px;
}

.diagnostic-row {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
}

.diagnostic-row strong,
.diagnostic-row span {
  display: block;
}

.diagnostic-row span {
  color: var(--muted);
  margin-top: 5px;
}

.diagnostic-row.ok {
  border-color: #386f47;
}

.diagnostic-row.warning {
  border-color: #78612d;
}

.diagnostic-row.error {
  border-color: #743434;
}
</style>
